import axios from "axios";
import CustomError from "../classes/CustomError";
import apiList from "@/assets/errors/apiList.json";

class CMApi {
  constructor(baseURL = "https://rest.cameramanager.com", token = null) {
    this.baseURL = baseURL;
    this.token = token;
    this.CancelToken = axios.CancelToken;
    this.source = this.CancelToken.source();
    this.apiList = apiList
  }

  timeout(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  getErrorCode(apiName) {
    const result = apiList.find(api => api.name === apiName).api_code
    return result ? result : 1
  }

  cancelAll() {
    this.source.cancel("restapi: cancelAll() was called");
    this.CancelToken = axios.CancelToken;
    this.source = this.CancelToken.source();
  }

  async ping() {
    try {
      const response = await axios.get("/rest/v2.0/ping", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      if (reason.response) {
        let error = reason.response.data;
        throw new CustomError(error.code, this.getErrorCode("PING"), error.detail);
      }
    }
  }

  async getAccountBase() {
    try {
      const response = await axios.get("/rest/v2.3/users/self", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_ACCOUNT_BASE"), error.detail);
    }
  }

  async setAccountBase(account) {
    try {
      await axios.patch(
        "/rest/v2.3/users/self",
        {
          email: account.email,
          firstName: account.firstName,
          lastName: account.lastName,
        },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return Promise.resolve(true);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_ACCOUNT_BASE"), error.detail);
    }
  }

  async getAccountDetails() {
    try {
      const response = await axios.get("/rest/v2.4/users/self/details", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_ACCOUNT_DETAILS"), error.detail);
    }
  }

  async setAccountDetails(accountDetails) {
    try {
      await axios.patch(
        "/rest/v2.3/users/self/details",
        {
          language: accountDetails.language,
          companyName: accountDetails.companyName,
          country: accountDetails.country,
          phone: accountDetails.phone,
        },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return true;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_ACCOUNT_DETAILS"), error.detail);
    }
  }

  async isPasswordValid(password) {
    try {
      const response = await axios.get(`/rest/v2.3/users/self/credentials?password=${encodeURIComponent(password)}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("PASSWORD_CORRECT"), error.detail);
    }
  }

  async setUserPassword(password) {
    try {
      await axios.patch(
        "/rest/v2.3/users/self/credentials",
        {
          usernameAndPassword: {
            password: password,
          },
        },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return true;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_USER_PASSWORD"), error.detail);
    }
  }

  async createSession() {
    try {
      const response = await axios.post("/rest/v2.0/users/self/sessions", null, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("CREATE_SESSION"), error.detail);
    }
  }

  async getAccountStatus() {
    try {
      const response = await axios.get("/rest/v2.4/users/self/status", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_ACCOUNT_STATUS"), error.detail);
    }
  }

  async setAccountStatus(allowsHelpdeskExpirationDate, allowsHelpdeskShowVideo) {
    try {
      await axios.patch(
        "/rest/v2.4/users/self/status",
        { allowsHelpdeskExpirationDate, allowsHelpdeskShowVideo },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return true;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_ACCOUNT_STATUS"), error.detail);
    }
  }

  async getLanguages(cache = true) {
    try {
      const response = await axios.get("/rest/v2.3/languages", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      const languages = [];
      const result = response.data;
      const txt = document.createElement("textarea");
      for (let i = 0; i < result.length; i++) {
        txt.innerHTML = result[i].name;
        const l = {
          value: result[i].key,
          text: txt.value,
        };
        languages.push(l);
      }
      return languages;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_LANGUAGES"), error.detail);
    }
  }

  async getCountries(cache = true) {
    try {
      const response = await axios.get("/rest/v2.3/countries", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      const countries = [];
      const result = response.data;
      for (let i = 0; i < result.length; i++) {
        const c = {
          value: result[i].code,
          text: result[i].name,
        };
        countries.push(c);
      }
      return countries;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_COUNTRIES"), error.detail);
    }
  }

  async getCamera(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.4/cameras/${cameraId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA"), error.detail);
    }
  }

  async setCameraName(cameraId, name) {
    try {
      const response = await axios.patch(
        `/rest/v2.1/cameras/${cameraId}`,
        { name: name },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_CAMERA_NAME"), error.detail);
    }
  }

  async getCameras() {
    try {
      const response = await axios.get("/rest/v2.4/cameras", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERAS"), error.detail);
    }
  }

  async getCameraStatus(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/status`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_STATUS"), error.detail);
    }
  }

  async getDeviceStatus(deviceId) {
    try {
      const response = await axios.get(`/rest/v2.4/devices/${deviceId}/status`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_DEVICE_STATUS"), error.detail);
    }
  }

  async getCamerasStatus() {
    try {
      const response = await axios.get("/rest/v2.2/cameras/all/status", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERAS_STATUS"), error.detail);
    }
  }

  async getCameraFirmware(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/firmware`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_FIRMWARE"), error.detail);
    }
  }

  async getDeviceFirmware(deviceId) {
    try {
      const response = await axios.get(`/rest/v2.4/devices/${deviceId}/firmware`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_DEVICE_FIRMWARE"), error.detail);
    }
  }

  async setDeviceFirmware(cameraId, version) {
    try {
      const response = await axios.post(
        `/rest/v2.4/devices/${cameraId}/firmware`,
        { currentVersion: version },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_DEVICE_FIRMWARE"), error.detail);
    }
  }

  async setCameraFirmware(cameraId, version) {
    try {
      const response = await axios.post(
        `/rest/v2.2/cameras/${cameraId}/firmware`,
        { currentVersion: version },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_CAMERA_FIRMWARE"), error.detail);
    }
  }

  async getSpecificUser(userID) {
    try {
      const response = await axios.get(`/rest/v2.4/users/${userID}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_USER"), error.detail);
    }
  }

  async setUserDetails(userID, data) {
    try {
      const response = await axios.patch(`/rest/v2.4/users/${userID}`, data, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("UPDATE_ZONE"), error.detail);
    }
  }

  async getSpecificUserRights(userID) {
    try {
      const response = await axios.get(`/rest/v2.4/users/${userID}/rights`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("USER_RIGHTS"), error.detail);
    }
  }
  async setSpecificUserRights(userID, permissions) {
    try {
      const response = await axios.patch(`/rest/v2.4/users/${userID}/rights`, permissions, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_USER_RIGHTS"), error.detail);
    }
  }
  async getCamerasFirmware() {
    try {
      const response = await axios.get("/rest/v2.2/cameras/all/firmware", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERAS_FIRMWARE"), error.detail);
    }
  }

  async getCameraWifiStatus(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.1/cameras/${cameraId}/wifi/status`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_WIFI_STATUS"), error.detail);
    }
  }

  async setCameraWifiStatus(cameraId, status) {
    try {
      const response = await axios.patch(`/rest/v2.1/cameras/${cameraId}/wifi/status`, status, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_CAMERA_WIFI_STATUS"), error.detail);
    }
  }

  async getCameraWifiVisible(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.1/cameras/${cameraId}/wifi/visible`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_WIFI_VISIBLE"), error.detail);
    }
  }

  async getCameraWifiSaved(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.1/cameras/${cameraId}/wifi/saved`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_WIFI_SAVED"), error.detail);
    }
  }

  async getCameraMobileNetwork(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.1/cameras/${cameraId}/mobile/network`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_MOBILE_NETWORK"), error.detail);
    }
  }

  async getCameraMobileModem(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.1/cameras/${cameraId}/mobile/modem`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_MOBILE_MODEM"), error.detail);
    }
  }

  async createCameraWifiSaved(cameraId, ssid, password) {
    try {
      const response = await axios.post(
        `/rest/v2.1/cameras/${cameraId}/wifi/saved`,
        { ssid: ssid, password: password },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("CREATE_CAMERA_WIFI_SAVED"), error.detail);
    }
  }

  async deleteCameraWifiSaved(cameraId, networkId) {
    try {
      const response = await axios.delete(`/rest/v2.1/cameras/${cameraId}/wifi/saved/${networkId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DELETE_CAMERA_WIFI_SAVED"), error.detail);
    }
  }

  async setCameraWifiSaved(cameraId, networkId) {
    try {
      const response = await axios.put(
        `/rest/v2.1/cameras/${cameraId}/wifi/saved/${networkId}`,
        {},
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_CAMERA_WIFI_SAVED"), error.detail);
    }
  }

  async getCameraStreams(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.3/cameras/${cameraId}/streams?includeUrlTypes=hlsHttps,mjpegHttps`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_STREAMS"), error.detail);
    }
  }

  async getCameraStreamsMulti(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/streams`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_STREAMS"), error.detail);
    }
  }

  async getDeviceTypes() {
    try {
      const response = await axios.get("/rest/v2.0/deviceTypes?includeCapabilities=true", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_DEVICE_TYPES"), error.detail);
    }
  }

  async getCameraDeviceInfo(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/deviceInfo`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_DEVICE_INFO"), error.detail);
    }
  }

  async getCameraDetectionCapabilities(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.0/cameras/${cameraId}/detectors/capabilities`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_DETECTION_CAPABILITIES"), error.detail);
    }
  }

  async getCameraDetectionSetting(cameraId, type) {
    try {
      const response = await axios.get(`/rest/v2.0/cameras/${cameraId}/detectors/${type}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return Promise.resolve(response.data.enabled);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_DETECTION_SETTING"), error.detail);
    }
  }

  async setCameraDetectionSetting(cameraId, type, enabled) {
    try {
      const response = await axios.put(
        `/rest/v2.0/cameras/${cameraId}/detectors/${type}`,
        { enabled: enabled },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_CAMERA_DETECTION_SETTING"), error.detail);
    }
  }

  async getCameraAnalyticsSetting(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.0/cameras/${cameraId}/detectors/analytics`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_ANALYTICS_SETTING"), error.detail);
    }
  }

  async setCameraAnalyticsSetting(cameraId, enabled, personDetectionOnly) {
    const settings = { enabled: enabled };
    if (personDetectionOnly !== null) {
      settings.personDetectionOnly = personDetectionOnly;
    }

    try {
      const response = await axios.put(`/rest/v2.0/cameras/${cameraId}/detectors/analytics`, settings, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_CAMERA_ANALYTICS_SETTING"), error.detail);
    }
  }

  async addCameraAnalyticsArea(cameraId, area) {
    try {
      const response = await axios.post(`/rest/v2.0/cameras/${cameraId}/detectors/analytics/areas`, area, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("ADD_CAMERA_ANALYTICS_AREA"), error.detail);
    }
  }

  async updateCameraAnalyticsArea(cameraId, areaId, area) {
    try {
      const response = await axios.patch(`/rest/v2.0/cameras/${cameraId}/detectors/analytics/areas/${areaId}`, area, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("UPDATE_CAMERA_ANALYTICS_AREA"), error.detail);
    }
  }

  async deleteCameraAnalyticsArea(cameraId, areaId) {
    try {
      const response = await axios.delete(`/rest/v2.0/cameras/${cameraId}/detectors/analytics/areas/${areaId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DELETE_CAMERA_ANALYTICS_AREA"), error.detail);
    }
  }

  async getCameraJobs(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.4/cameras/${cameraId}/jobs`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_JOBS"), error.detail);
    }
  }

  async getDevicesJobs(deviceId) {
    try {
      const response = await axios.get(`/rest/v2.4/devices/${deviceId}/jobs`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_DEVICES_JOBS"), error.detail);
    }
  }


  async getCameraJob(cameraId, jobId) {
    try {
      const response = await axios.get(`/rest/v2.4/cameras/${cameraId}/jobs/${jobId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_JOB"), error.detail);
    }
  }

  async ptzCameraControl(cameraId, direction) {
    try {
      const response = await axios.post(
        `/rest/v2.1/cameras/${cameraId}/ptz`,
        {
          moveType: "CameraPtzDirectionMove",
          direction: direction,
        },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("MOVE_PTZ_CAMERA"), error.detail);
    }
  }

  async getJNLPFile(language) {
    if (!language) language = "en";

    try {
      const response = await axios.get(`rest/v2.2/users/self/applications/desktop?languageKey=${language}`, {
        responseType: "blob",
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: {
          Authorization: `Bearer ${this.token}`,
          Accept: "application/json,application/x-java-jnlp-file",
        },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_JNLP_FILE"), error.detail);
    }
  }

  async getAllCamerasFootageSettings(cameras) {
    try {
      let camerasObj = [ ...cameras ].filter(cam => cam.status.online).slice(0,10)
      let promises = camerasObj.map(camera => {
        return this.getCameraFootageSettings(camera.cameraId)
      })
      const results = await Promise.allSettled(promises)
      let formattedResults = {}
      results.forEach((result, index) => {
        const cameraId = camerasObj[index].cameraId
        formattedResults[cameraId] = result.status === "fulfilled" ? result.value : null
      })
      return formattedResults
    } catch (reason) {
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_FOOTAGE_SETTINGS"), error.detail);
    }
  }

  async getCameraFootageSettings(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/footage/settings`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_FOOTAGE_SETTINGS"), error.detail);
    }
  }

  async setCameraFootageSettings(cameraId, settings) {
    try {
      const response = await axios.patch(`/rest/v2.2/cameras/${cameraId}/footage/settings`, settings, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_CAMERA_FOOTAGE_SETTINGS"), error.detail);
    }
  }

  async getCameraFootageCapabilities(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/footage/capabilities`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_FOOTAGE_CAPABILITIES"), error.detail);
    }
  }

  async getCameraCapabilities(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/capabilities`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_CAPABILITIES"), error.detail);
    }
  }

  async getCameraImageCapabilities(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/capabilities/image`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_IMAGE_CAPABILITIES"), error.detail);
    }
  }

  async getCameraWifiCapabilities(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/capabilities/wifi`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_WIFI_CAPABILITIES"), error.detail);
    }
  }

  async getCameraSystemCapabilities(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/capabilities/system`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_SYSTEM_CAPABILITIES"), error.detail);
    }
  }

  async getCameraPTZCapabilities(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/capabilities/ptz`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_PTZ_CAPABILITIES"), error.detail);
    }
  }

  async getZones(zoneId) {
    try {
      const response = await axios.get("/rest/v2.1/zones" + (zoneId ? `/${zoneId}` : ""), {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_ZONES"), error.detail);
    }
  }

  async setZoneCameras(cameraIds, zoneId) {
    try {
      const response = await axios.patch(`/rest/v2.1/zones/${zoneId}/cameraIds`, cameraIds, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_ZONE_CAMERAS"), error.detail);
    }
  }

  async getDeviceType(typeId) {
    try {
      const response = await axios.get(`/rest/v2.0/deviceTypes/${typeId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_DEVICE_TYPE"), error.detail);
    }
  }

  async getCameraDetails(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/details`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_DETAILS"), error.detail);
    }
  }

  async getCamerasMode() {
    try {
      const response = await axios.get("/rest/v2.2/cameras/all/mode", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERAS_MODE"), error.detail);
    }
  }

  async getCameraMode(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/mode`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_MODE"), error.detail);
    }
  }

  async setCameraMode(cameraId, mode) {
    try {
      const response = await axios.patch(`/rest/v2.2/cameras/${cameraId}/mode`, mode, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_CAMERA_MODE"), error.detail);
    }
  }

  async getCameraMicrophoneEnabled(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.1/cameras/${cameraId}/microphone`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data.enabled;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_MICROPHONE_ENABLED"), error.detail);
    }
  }

  async getCamerasMicrophoneEnabled() {
    try {
      const response = await axios.get("/rest/v2.3/cameras/all/microphone", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERAS_MICROPHONE_ENABLED"), error.detail);
    }
  }

  async setCameraMicrophoneEnabled(cameraId, enabled) {
    try {
      const response = await axios.put(
        `/rest/v2.1/cameras/${cameraId}/microphone`,
        { enabled: enabled },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return Promise.resolve(response.data.enabled);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_CAMERA_MICROPHONE_ENABLED"), error.detail);
    }
  }

  async addZone(zoneName) {
    try {
      const response = await axios.post(
        "/rest/v2.1/zones",
        { name: zoneName },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("ADD_ZONE"), error.detail);
    }
  }

  async setZoneName(zoneId, zoneName) {
    try {
      const response = await axios.patch(
        `/rest/v2.1/zones/${zoneId}`,
        { name: zoneName },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("UPDATE_ZONE"), error.detail);
    }
  }

  async setZoneAddress(zoneId, address) {
    try {
      const response = await axios.patch(
        `/rest/v2.1/zones/${zoneId}`,
        { address },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("UPDATE_ZONE"), error.detail);
    }
  }

  async setZonePhone(zoneId, primaryPhoneNumber, SecondaryPhoneNumber) {
    try {
      const response = await axios.patch(
        `/rest/v2.1/zones/${zoneId}`,
        { phone: [primaryPhoneNumber, SecondaryPhoneNumber] },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("UPDATE_ZONE"), error.detail);
    }
  }

  async deleteZone(zoneId) {
    try {
      const response = await axios.delete(`/rest/v2.1/zones/${zoneId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DELETE_ZONE"), error.detail);
    }
  }

  async deleteCameraWithoutPassword(cameraId) {
    try {
      const response = await axios.delete(`/rest/v2.1/cameras/${cameraId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DELETE_CAMERA_WITHOUT_PASSWORD"), error.detail);
    }
  }

  async getCameraConnectionCredentials(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/connection/credentials`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_CONNECTION_CREDENTIALS"), error.detail);
    }
  }

  async setCameraConnectionCredentials(cameraId, credentials, skipValidation = false) {
    try {
      await axios.patch(
        `/rest/v2.2/cameras/${cameraId}/connection/credentials?skipValidation=` + (skipValidation ? "true" : "false"),
        credentials,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return true;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_CAMERA_CONNECTION_CREDENTIALS"), error.detail);
    }
  }

  async getCameraVideoSettings(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/video/settings`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_VIDEO_SETTINGS"), error.detail);
    }
  }

  async setCameraVideoSettings(cameraId, settings) {
    try {
      await axios.patch(`/rest/v2.2/cameras/${cameraId}/video/settings`, settings, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return true;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_CAMERA_VIDEO_SETTINGS"), error.detail);
    }
  }

  async deleteCameraVideoSettings(cameraId) {
    try {
      const response = await axios.delete(`/rest/v2.2/cameras/${cameraId}/video/settings`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DELETE_CAMERA_VIDEO_SETTINGS"), error.detail);
    }
  }

  async getCameraConnectionDetails(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.2/cameras/${cameraId}/connection/details`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_CONNECTION_DETAILS"), error.detail);
    }
  }

  async setCameraConnectionDetails(cameraId, details) {
    try {
      await axios.patch(`/rest/v2.2/cameras/${cameraId}/connection/details?skipValidation=true`, details, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return true;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_CAMERA_CONNECTION_DETAILS"), error.detail);
    }
  }

  async getResellers(distributorId) {
    try {
      const response = await axios.get(
        "/rest/v2.0/resellers" + (distributorId !== undefined ? "?distributorId=" + distributorId : ""),
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      response.data.filter(function (r) {
        return r.resellerId;
      });
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_RESELLERS"), error.detail);
    }
  }

  async getReseller(id) {
    try {
      const response = await axios.get(`/rest/v2.0/resellers/${id}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_RESELLER"), error.detail);
    }
  }

  async getResellerCount(id) {
    try {
      const response = await axios.get(`/rest/v2.0/resellers/${id}/count`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_RESELLER_COUNT"), error.detail);
    }
  }

  async getResellerDetails(id) {
    try {
      const response = await axios.get(`/rest/v2.0/resellers/${id}/details`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_RESELLER_COUNT"), error.detail);
    }
  }

  async setReseller(id, reseller) {
    try {
      const response = await axios.patch(`/rest/v2.0/resellers/${id}`, reseller, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_RESELLER"), error.detail);
    }
  }

  async setResellerDetails(id, reseller) {
    try {
      await axios.patch(`/rest/v2.0/resellers/${id}/details`, reseller, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return true;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_RESELLER_DETAILS"), error.detail);
    }
  }

  async addReseller(reseller) {
    try {
      const response = await axios.post("/rest/v2.0/resellers", reseller, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      const partner = response.data;
      const id = partner.resellerId;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("ADD_RESELLER"), error.detail);
    }
  }

  async moveResellerToDistributor(resellerId, distributorId) {
    try {
      const response = await axios.put(`/rest/v2.0/resellers/${resellerId}/distributor/${distributorId}`, null, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      if (reason.response && reason.response.status === 409) {
        return new Error(409);
      } else {
        console.error(reason);
        let error = reason.response.data;
        throw new CustomError(error.code, this.getErrorCode("MOVE_RESELLER"), error.detail);
      }
    }
  }

  async getDistributors() {
    try {
      const response = await axios.get("/rest/v2.0/distributors", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_DISTRIBUTORS"), error.detail);
    }
  }

  async getDistributor(id) {
    try {
      const response = await axios.get(`/rest/v2.0/distributors/${id}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_DISTRIBUTOR"), error.detail);
    }
  }

  async getDistributorCount(id) {
    try {
      const response = await axios.get(`/rest/v2.0/distributors/${id}/count`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_DISTRIBUTOR_COUNT"), error.detail);
    }
  }

  async getDistributorDetails(id) {
    try {
      const response = await axios.get(`/rest/v2.0/distributors/${id}/details`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_DISTRIBUTOR_DETAILS"), error.detail);
    }
  }

  async setDistributor(id, distributor) {
    try {
      const response = await axios.patch(`/rest/v2.0/distributors/${id}`, distributor, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_DISTRIBUTOR"), error.detail);
    }
  }

  async setDistributorDetails(id, distributor) {
    try {
      await axios.patch(`/rest/v2.0/distributors/${id}/details`, distributor, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return true;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_DISTRIBUTOR_DETAILS"), error.detail);
    }
  }

  async addDistributor(distributor) {
    try {
      const response = await axios.post("/rest/v2.0/distributors", distributor, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      const partner = response.data;
      const id = partner.distributorId;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("ADD_DISTRIBUTOR"), error.detail);
    }
  }

  getPartner(id, type) {
    if (type.toLowerCase() === "reseller") {
      return this.getReseller(id);
    } else if (type.toLowerCase() === "distributor") {
      return this.getDistributor(id);
    }
  }

  setPartner(id, type, partner) {
    if (type.toLowerCase() === "reseller") {
      return this.setReseller(id, partner);
    } else if (type.toLowerCase() === "distributor") {
      return this.setDistributor(id, partner);
    }
  }

  getPartnerCount(id, type) {
    if (type.toLowerCase() === "reseller") {
      return this.getResellerCount(id);
    } else if (type.toLowerCase() === "distributor") {
      return this.getDistributorCount(id);
    }
  }

  getPartnerDetails(id, type) {
    if (type.toLowerCase() === "reseller") {
      return this.getResellerDetails(id);
    } else if (type.toLowerCase() === "distributor") {
      return this.getDistributorDetails(id);
    }
  }

  setPartnerDetails(id, type, partner) {
    if (type.toLowerCase() === "reseller") {
      return this.setResellerDetails(id, partner);
    } else if (type.toLowerCase() === "distributor") {
      return this.setDistributorDetails(id, partner);
    }
  }

  addPartner(type, partner) {
    if (type.toLowerCase() === "reseller") {
      return this.addReseller(partner);
    } else if (type.toLowerCase() === "distributor") {
      return this.addDistributor(partner);
    }
  }

  async getAccounts(resellerId, accountId, from = null, limit = 200, active = null) {
    try {
      const response = await axios.get(
        `/rest/v2.0/resellers/${resellerId}/accounts` +
          (accountId
            ? `/${accountId}`
            : "?" + (from ? `from=${from}&` : "") + `limit=${limit}` + (active !== null ? `&active=${active}` : "")),
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      const account = response.data;
      if (accountId) {
        const attributes = ["city", "zipCode", "currency", "blocked"];
        attributes.forEach(function (attribute) {
          if (!account.hasOwnProperty(attribute)) account[attribute] = null;
        });
      }
      return account;
    } catch (reason) {
      if (reason.response && reason.response.status === 404) {
        return Promise.resolve(null);
      } else {
        console.error(reason);
        let error = reason.response.data;
        throw new CustomError(error.code, this.getErrorCode("GET_ACCOUNTS"), error.detail);
      }
    }
  }

  async getAccountUsers(resellerId, accountId, userId) {
    try {
      const response = await axios.get(
        `/rest/v2.0/resellers/${resellerId}/accounts/${accountId}/users` + (userId ? `/${userId}` : ""),
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_ACCOUNT_USERS"), error.detail);
    }
  }

  async getAccountOwner(resellerId, accountId) {
    try {
      const response = await axios.get(`/rest/v2.0/resellers/${resellerId}/accounts/${accountId}/users?role=owner`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return Promise.resolve(response.data[0]);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_ACCOUNT_OWNER"), error.detail);
    }
  }

  async addAccount(resellerId, data) {
    try {
      const response = await axios.post(`/rest/v2.0/resellers/${resellerId}/accounts`, data, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("ADD_ACCOUNT"), error.detail);
    }
  }

  async setAccount(resellerId, accountId, data) {
    try {
      const response = await axios.patch(`/rest/v2.0/resellers/${resellerId}/accounts/${accountId}`, data, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_ACCOUNT"), error.detail);
    }
  }

  async setAccountUser(resellerId, accountId, userId, data) {
    try {
      const response = await axios.patch(
        `/rest/v2.0/resellers/${resellerId}/accounts/${accountId}/users/${userId}`,
        data,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_ACCOUNT_USER"), error.detail);
    }
  }

  async setAccountUserPassword(resellerId, accountId, userId, password) {
    try {
      const response = await axios.patch(
        `/rest/v2.0/resellers/${resellerId}/accounts/${accountId}/users/${userId}/credentials`,
        {
          usernameAndPassword: {
            password: password,
          },
        },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_ACCOUNT_USER_PASSWORD"), error.detail);
    }
  }

  async getApiKeys() {
    try {
      const response = await axios.get("/rest/v2.0/accounts/self/credentials/oauthclients", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_API_KEYS"), error.detail);
    }
  }

  async getApiKeysByPartnerId(partnerId, clientId = null) {
    const apiCall = `/rest/v2.0/accounts/${partnerId}/credentials/oauthclients` + (clientId ? `/${clientId}` : "");

    try {
      const response = await axios.get(apiCall, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_API_KEYS_PARTNER"), error.detail);
    }
  }

  async createApiKey(partnerId, clientId) {
    try {
      const response = await axios.post(
        "/rest/v2.0/accounts/" + partnerId + "/credentials/oauthclients",
        { clientId: clientId },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("CREATE_API_KEYS"), error.detail);
    }
  }

  async deleteApiKeys(partnerId) {
    try {
      const response = await axios.delete(`/rest/v2.0/accounts/${partnerId}/credentials/oauthclients`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DELETE_API_KEYS"), error.detail);
    }
  }

  async deleteApiKey(partnerId, clientId) {
    try {
      const response = await axios.delete(`/rest/v2.0/accounts/${partnerId}/credentials/oauthclients/${clientId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DELETE_API_KEYS"), error.detail);
    }
  }

  async getAccountSubscription(userId) {
    try {
      const response = await axios.get(`/rest/v2.4/accounts/${userId}/subscriptions/current`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_ACCOUNT_SUBSCRIPTION"), error.detail);
    }
  }

  async getAccountSubscriptions(accountId) {
    try {
      const response = await axios.get(`/rest/v2.4/accounts/${accountId}/subscriptions`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_ACCOUNT_SUBSCRIPTIONS"), error.detail);
    }
  }

  async getCameraSlotsAvailable() {
    try {
      const response = await axios.get(`/rest/v2.1/accounts/self/subscription/cameraslots`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("CHECK_CAMERA_SLOTS"), error.detail);
    }
  }

  async updateAccountSubscription(resellerId, userId, nextSubscriptionId, effectiveToday = null) {
    const data = {
      nextSubscriptionId: nextSubscriptionId,
    };

    if (effectiveToday !== null) {
      data.effectiveToday = effectiveToday;
    }

    try {
      const response = await axios.patch(
        `/rest/v2.0/resellers/${resellerId}/accounts/${userId}/subscriptions/current`,
        data,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("UPDATE_ACCOUNT_SUBSCRIPTION"), error.detail);
    }
  }

  async updateMyAccountSubscription(userId, nextSubscriptionPackageId,effectiveToday = null) {
    const data = {
      nextSubscriptionPackageId: nextSubscriptionPackageId
    };

    if (effectiveToday !== null) {
      data.effectiveToday = effectiveToday;
    }

    try {
      const response = await axios.patch(
        `/rest/v2.4/accounts/${userId}/subscriptions/current`,
        data,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("UPDATE_ACCOUNT_SUBSCRIPTION"), error.detail);
    }
  }

  async cancelAccountSubscription(resellerId, userId, effectiveToday = null) {
    const data = {
      deactivate: true,
    };

    if (effectiveToday !== null) {
      data.effectiveToday = effectiveToday;
    }

    try {
      const response = await axios.patch(
        `/rest/v2.0/resellers/${resellerId}/accounts/${userId}/subscriptions/current`,
        data,
        {
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("CANCEL_ACCOUNT_SUBSCRIPTION"), error.detail);
    }
  }

  async downloadHelpdeskJnlp(accountId, userId) {
    try {
      const response = await axios.get(
        `rest/v2.1/resellers/self/accounts/${accountId}/users/${userId}/applications/desktop/helpdesk`,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${this.token}`,
            Accept: "application/json,application/x-java-jnlp-file",
          },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DOWNLOAD_HELPDESK_JNLP"), error.detail);
    }
  }

  async getMobileHelpdeskUrl(accountId, userId) {
    try {
      const response = await axios.get(
        `rest/v2.2/resellers/self/accounts/${accountId}/users/${userId}/applications/mobile/helpdesk`,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_MOBILE_HELPDESK_URL"), error.detail);
    }
  }

  async getResellerSubscriptions(resellerId, subscriptionId) {
    try {
      const response = await axios.get(
        `/rest/v2.0/resellers/${resellerId}/subscriptions` + (subscriptionId ? `/${subscriptionId}` : ""),
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_RESELLER_SUBSCRIPTIONS"), error.detail);
    }
  }

  async getCamerasSummary(resellerId, accountId) {
    try {
      const response = await axios.get(`/rest/v2.1/resellers/${resellerId}/accounts/${accountId}/cameras/all/summary`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERAS_SUMMARY"), error.detail);
    }
  }

  async getCamerasDetails(resellerId, accountId, cameraId) {
    try {
      const response = await axios.get(
        `/rest/v2.1/resellers/${resellerId}/accounts/${accountId}/cameras/${cameraId}/summary/details`,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERAS_DETAILS"), error.detail);
    }
  }

  async moveAccountToReseller(resellerId, accountId, targetResellerId, subscriptionId) {
    const data = {
      subscriptionId: subscriptionId,
    };

    try {
      const response = await axios.put(
        `/rest/v2.1/resellers/${resellerId}/accounts/${accountId}/reseller/${targetResellerId}`,
        data,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("MOVE_ACCOUNT_TO_RESELLER"), error.detail);
    }
  }

  async getResellerModules(resellerId) {
    try {
      const response = await axios.get(`/rest/v2.1/resellers/${resellerId}/modules`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_RESELLER_MODULES"), error.detail);
    }
  }

  async getPolicyData(partnerId) {
    try {
      const response = await axios.get(`/rest/v2.1/accounts/${partnerId}/policyAcceptance`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_POLICY_DATA"), error.detail);
    }
  }

  async addPolicy(partnerId, version, accepted) {
    const data = {
      version: version,
      accepted: accepted,
    };

    try {
      const response = await axios.post(`/rest/v2.1/accounts/${partnerId}/policyAcceptance`, data, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("ADD_POLICY"), error.detail);
    }
  }

  async updatePolicy(partnerId, version, accepted) {
    const data = {
      version: version,
      accepted: accepted,
    };

    try {
      const response = await axios.patch(`/rest/v2.1/accounts/${partnerId}/policyAcceptance`, data, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("UPDATE_POLICY"), error.detail);
    }
  }

  async searchUser(resellerId, accountId, search, limit = 999) {
    const searchEncoded = encodeURIComponent(search);

    try {
      const response = await axios.get(
        `/rest/v2.1/searchUser?partnerId=${resellerId}&accountId=${accountId}&searchString=${searchEncoded}&limit=${limit}`,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SEARCH_USER"), error.detail);
    }
  }

  async getReportInfo(partnerId, reportType) {
    try {
      const response = await axios.get(
        `/rest/v2.0/reports/partner/self/${reportType}/today` + (partnerId ? `?resellerId=${partnerId}` : ""),
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_REPORT_INFO"), error.detail);
    }
  }

  async downloadReport(partnerId, reportType, partnerName, generationDate, downloadName) {
    const data = {
      baseURL: this.baseURL,
      cancelToken: this.source.token,
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${this.token}`,
        Accept: "application/json,text/csv",
      },
    };

    try {
      const response = await axios.get(
        `/rest/v2.0/reports/partner/self/${reportType}/today/csv` + (partnerId ? `?resellerId=${partnerId}` : ""),
        data
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DOWNLOAD_REPORT"), error.detail);
    }
  }

  async getPartnerAccounts(partnerId) {
    try {
      const response = await axios.get(`/rest/v2.2/accounts/${partnerId}/users`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_PARTNER_ACCOUNTS"), error.detail);
    }
  }

  async getPartnerAccount(partnerId, userId) {
    try {
      const response = await axios.get(`/rest/v2.2/accounts/${partnerId}/users/${userId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_PARTNER_ACCOUNT"), error.detail);
    }
  }

  async addPartnerAccount(partnerId, account) {
    try {
      const response = await axios.post(`/rest/v2.2/accounts/${partnerId}/users`, account, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("ADD_PARTNER_ACCOUNT"), error.detail);
    }
  }

  async updatePartnerAccount(partnerId, userId, account) {
    try {
      const response = await axios.patch(`/rest/v2.2/accounts/${partnerId}/users/${userId}`, account, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("UPDATE_PARTNER_ACCOUNT"), error.detail);
    }
  }

  async deletePartnerAccount(partnerId, userId) {
    try {
      await axios.delete(`/rest/v2.2/accounts/${partnerId}/users/${userId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return Promise.resolve(true);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DELETE_PARTNER_ACCOUNT"), error.detail);
    }
  }

  async getUserToken(accountId, userId) {
    try {
      const response = await axios.post(`/rest/v2.2/resellers/self/accounts/${accountId}/users/${userId}/token`, null, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_USER_TOKEN"), error.detail);
    }
  }

  async getCurrentUser() {
    try {
      const response = await axios.get("/rest/v2.2/users/self", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CURRENT_USER"), error.detail);
    }
  }

  async getUserRights() {
    try {
      const response = await axios.get("/rest/v2.2/users/self/rights", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_USER_RIGHTS"), error.detail);
    }
  }

  async getAccountProfiles(accountId, profileId) {
    try {
      const response = await axios.get(
        `/rest/v2.2/accounts/${accountId}/permissionProfiles` + (profileId ? `/${profileId}` : ""),
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_ACCOUNT_PROFILES"), error.detail);
    }
  }

  async createAccountProfile(accountId, profile) {
    try {
      const response = await axios.post(`/rest/v2.2/accounts/${accountId}/permissionProfiles`, profile, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("CREATE_ACCOUNT_PROFILE"), error.detail);
    }
  }

  async updateAccountProfile(accountId, profileId, profile) {
    try {
      const response = await axios.patch(`/rest/v2.2/accounts/${accountId}/permissionProfiles/${profileId}`, profile, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("UPDATE_ACCOUNT_PROFILE"), error.detail);
    }
  }

  async deleteAccountProfile(accountId, profileId) {
    try {
      const response = await axios.delete(`/rest/v2.2/accounts/${accountId}/permissionProfiles/${profileId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DELETE_ACCOUNT_PROFILE"), error.detail);
    }
  }

  async updateAccountProfilePermissions(accountId, profileId, permissions) {
    try {
      const response = await axios.patch(
        `/rest/v2.2/accounts/${accountId}/permissionProfiles/${profileId}/permissions`,
        permissions,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("UPDATE_ACCOUNT_PROFILE_PERMISSIONS"), error.detail);
    }
  }

  async getUserProfile(userId) {
    try {
      const response = await axios.get(`/rest/v2.2/users/${userId}/permissionProfiles`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return Promise.resolve(response.data.provProfileId);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_USER_PROFILE"), error.detail);
    }
  }

  async getSnapshot(cameraId, resolution = "1920x1080", timestamp = null, includeTimestamp = false) {
    if (resolution === null) resolution = "1920x1080";
    try {
      const response = await axios.get(
        `/rest/v2.4/cameras/${cameraId}/snapshot?resolution=${resolution}` +
          (timestamp ? `&timestamp=${encodeURIComponent(timestamp)}` : "") + `&includeTimestamp=${includeTimestamp}`,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          responseType: "arraybuffer",
          headers: {
            Accept: "image/jpeg",
            Authorization: `Bearer ${this.token}`,
          },
        }
      );
      const image = Buffer.from(response.data).toString("base64");
      return Promise.resolve(`data:${response.headers["content-type"].toLowerCase()};base64, ${image}`);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_SNAPSHOT"), error.detail);
    }
  }

  async getStorageID(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.4/cameras/${cameraId}/edgeStorages`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return Promise.resolve(response);
    } catch (reason) {
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_EDGE_STORAGE"), error.detail);
    }
  }

  async formatEdgeStorage(cameraId, storageId) {
    try {
      const response = await axios.post(`/rest/v2.4/cameras/${cameraId}/edgeStorages/${storageId}/formatJobs`, null, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return Promise.resolve(response);
    } catch (reason) {
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("FORMAT_EDGE"), error.detail);
    }
  }

  async getCameraSubscriptionRights(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.0/cameras/${cameraId}/subscriptionRights`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_SUBSCRIPTION_RIGHTS"), error.detail);
    }
  }

  async getCamerasSubscriptionRights() {
    try {
      const response = await axios.get("/rest/v2.3/cameras/all/subscriptionRights", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_SUBSCRIPTION_RIGHTS"), error.detail);
    }
  }

  async getAllCameraRecordings(cameraId, fromTs, tillTs, requestId, callback = () => {}) {
    let recordings = [];
    let tillTime = tillTs || new Date().getTime() + 3600 * 1000;
    const fromTime = fromTs || tillTime - 7 * 86400 * 1000;

    let cancelled = false;
    this.source.token.promise.then(() => {
      cancelled = true;
    });

    while (tillTime >= fromTime && !cancelled) {
      const maxTs = new Date(tillTime).toISOString().replace("Z", "+0000");
      const chunk = await this.getCameraRecordings(cameraId, null, maxTs, 200, requestId);
      if (chunk && chunk.length > 0) {
        callback(chunk);
        recordings = [...recordings, ...chunk];
        tillTime = new Date(chunk[chunk.length - 1].startTime).getTime() - 1;
      }
      if (chunk === null || chunk.length === 0) break;
    }

    return cancelled ? [] : recordings;
  }

  async getCameraRecordings(cameraId, minTimestamp, maxTimestamp, limit, requestId) {
    try {
      const response = await axios.get(
        `/rest/v2.2/cameras/${cameraId}/recordings?includeUrlType=mp4Https,multipartAudioHttps&sortByRecordingIdOrder=desc` +
          (limit ? `&limit=${limit}` : "") +
          (minTimestamp ? `&minTimestamp=${encodeURIComponent(minTimestamp)}` : "") +
          (maxTimestamp ? `&maxTimestamp=${encodeURIComponent(maxTimestamp)}` : ""),
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
          requestId: requestId,
        }
      );
      const recordings = response.data;
      recordings.map((r) => {
        r.startTime = r.startTime.replace("+0000", "Z");
        if (r.hasOwnProperty("endTime")) r.endTime = r.endTime.replace("+0000", "Z");
      });
      return recordings;
    } catch (reason) {
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_RECORDINGS"), error.detail);
    }
  }

  async getCameraRecording(cameraId, recordingId, sliceStartTimestamp, requestId) {
    try {
      const response = await axios.get(
        `/rest/v2.4/cameras/${cameraId}/recordings/${recordingId}` +
          (sliceStartTimestamp
            ? `/parts?sliceStartTimestamp=${encodeURIComponent(sliceStartTimestamp)}&includeUrlTypes=mp4Https`
            : "?includeUrlTypes=mp4Https"),
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
          requestId: requestId,
        }
      );
      const chunks = response.data;
      for (let i = 0; i < chunks.length; i++) {
        const recording = chunks[i];
        recording.urls.mp4Https = recording.urls.mp4Https.concat("&strict=false");
        recording.startTime = recording.startTime.replace("+0000", "Z");
        if (recording.hasOwnProperty("endTime")) recording.endTime = recording.endTime.replace("+0000", "Z");
      }
      return Promise.resolve(chunks);
    } catch (reason) {
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_RECORDING"), error.detail);
    }
  }

  async getFirstCameraRecording(cameraId, requestId) {
    const maxTimestamp = new Date().toISOString().replace("Z", "+0000");
    try {
      const response = await axios.get(
        `/rest/v2.2/cameras/${cameraId}/recordings?sortByRecordingIdOrder=asc&limit=10&maxTimestamp=${encodeURIComponent(
          maxTimestamp
        )}`,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
          requestId: requestId,
        }
      );
      const recording = response.data.length > 0 ? response.data[0] : null;
      if (recording) {
        recording.startTime = recording.startTime.replace("+0000", "Z");
        if (recording.hasOwnProperty("endTime")) recording.endTime = recording.endTime.replace("+0000", "Z");
      }
      return Promise.resolve(recording);
    } catch (reason) {
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_RECORDING"), error.detail);
    }
  }
  async getMp4Url(cameraId, recordingId) {
    try {
      const response = await axios.get(
        `/rest/v2.2/cameras/${cameraId}/recordings/${recordingId}?includeUrlType=mp4Https`,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return { data: response.data, token: this.token };
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("REQUEST_MP4_URL"), error.detail);
    }
  }
  async getMp4(cameraId, minTimestamp, maxTimestamp, recordingId) {
    console.log(minTimestamp, maxTimestamp);
    const from = encodeURIComponent(minTimestamp.slice(0, -1) + "+0000");
    const till = encodeURIComponent(maxTimestamp.slice(0, -1) + "+0000");
    try {
      const response = await axios.get(
        `/cameras/${cameraId}/recordings/${recordingId}.mp4?access_token=${this.token}&from=${from}&till=${till}`,
        {
          baseURL: "https://ca012.cameramanager.com:443/rest",
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_MP4"), error.detail);
    }
  }

  async getAllEvents(fromTs, tillTs, limit, db) {
    const chunkSize = 200;
    let chunk = null;
    let events = [];
    let startTime = tillTs || new Date().getTime();
    const endTime = fromTs || startTime - 7 * 86400 * 1000;
    let maxNofEvents = limit || chunkSize;

    while (maxNofEvents > 0 && startTime >= endTime) {
      this.timeout(1000);
      chunk = await this.getEvents(
        null,
        new Date(startTime).toISOString().replace("Z", "+0000"),
        Math.min(maxNofEvents, chunkSize)
      );
      if (chunk && chunk.length > 0) {
        if (db) {
          console.log(`Adding ${chunk.length} events to the indexdb`);
          for (let i = 0; i < chunk.length; i++) {
            const event = chunk[i];
            db.events.put({
              eventId: event.eventId,
              cameraId: event.source.sourceId,
              type: event.type,
              timestamp: new Date(event.timestamp).getTime(),
            });
          }
        }
        events = [...events, ...chunk];
        if (limit) maxNofEvents -= chunk.length;
        startTime = new Date(chunk[chunk.length - 1].timestamp).getTime() - 1;
      }
      if (chunk === null || chunk.length < chunkSize) break;
    }

    return events;
  }

  async getEvents(minTimestamp, maxTimestamp, limit, requestId) {
    try {
      const response = await axios.get(
        "/rest/v2.2/events?sortBy=eventId.desc" +
          (limit ? `&limit=${limit}` : "") +
          (minTimestamp ? `&minTimestamp=${encodeURIComponent(minTimestamp)}` : "") +
          (maxTimestamp ? `&maxTimestamp=${encodeURIComponent(maxTimestamp)}` : ""),
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
          requestId: requestId,
        }
      );
      const events = response.data;
      events.map((e) => {
        e.timestamp = e.timestamp.replace("+0000", "Z");
      });
      return Promise.resolve(events);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_EVENTS"), error.detail);
    }
  }

  async getFirstEvent(cameraIds, requestId) {
    const maxTimestamp = new Date().toISOString().replace("Z", "+0000");
    try {
      const response = await axios.get(
        `/rest/v2.2/events?sortBy=eventId.asc&limit=1&maxTimestamp=${encodeURIComponent(maxTimestamp)}` +
          (cameraIds ? `&cameraIds=${cameraIds}` : ""),
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
          requestId: requestId,
        }
      );
      const event = response.data.length > 0 ? response.data[0] : null;
      if (event) event.timestamp = event.timestamp.replace("+0000", "Z");
      return event;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_FIRST_EVENT"), error.detail);
    }
  }

  async getAllCameraEvents(cameraId, fromTs, tillTs, limit, requestId, callback = () => {}) {
    const chunkSize = 200;
    let chunk = null;
    let events = [];
    let startTime = tillTs || new Date().getTime();
    const endTime = fromTs || startTime - 7 * 86400 * 1000;
    let maxNofEvents = limit || chunkSize;

    while (maxNofEvents > 0 && startTime >= endTime) {
      chunk = await this.getCameraEvents(
        cameraId,
        null,
        new Date(startTime).toISOString().replace("Z", "+0000"),
        Math.min(maxNofEvents, chunkSize),
        requestId
      );
      if (chunk && chunk.length > 0) {
        callback(chunk);
        events = [...events, ...chunk];
        if (limit) maxNofEvents -= chunk.length;
        startTime = new Date(chunk[chunk.length - 1].timestamp).getTime() - 1;
      }
      if (chunk === null || chunk.length < chunkSize) break;
    }

    return events;
  }

  async getAllNewCameraEvents(cameraId, fromTs, tillTs, limit, requestId, eventTypes, callback = () => {}) {
    try {
      const chunkSize = 200;
      let chunk = null;
      let events = [];
      let startTime = tillTs || new Date().getTime();
      const endTime = fromTs || startTime - 7 * 86400 * 1000;
      let maxNofEvents = limit || chunkSize;

      while (maxNofEvents > 0 && startTime >= endTime) {
        chunk = await this.getNewCameraEvents(
          cameraId,
          null,
          new Date(startTime).toISOString().replace("Z", "+0000"),
          Math.min(maxNofEvents, chunkSize),
          requestId,
          eventTypes
        );
        if (chunk && chunk.length > 0) {
          callback(chunk);
          events = [...events, ...chunk];
          if (limit) maxNofEvents -= chunk.length;
          startTime = new Date(chunk[chunk.length - 1].timestamp).getTime() - 1;
        }
        if (chunk === null || chunk.length < chunkSize) break;
      }
      return events 
    } catch (error) {
      console.log("Events couldn't be loaded")
    }
  }

  async getRecordingConfiguration(cameraId, storageId) {
    try {
      const response = await axios.get(
        `rest/v2.4/cameras/${cameraId}/edgeStorages/${storageId}/recordingConfiguration`,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_RECORDING_CONFIGURATION"), error.detail);
    }
  }

  async patchRecordingConfiguration(cameraId, storageId, autoSyncEnabled) {
    try {
      const response = await axios.patch(
        `rest/v2.4/cameras/${cameraId}/edgeStorages/${storageId}/recordingConfiguration`,
        {
          autoSyncEnabled,
        },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("PATCH_RECORDING_CONFIGURATION"), error.detail);
    }
  }

  async getNewCameraEvents(
    cameraId,
    minTimestamp,
    maxTimestamp,
    limit,
    requestId,
    types,
    streams
  ) {
    try {
      const response = await axios.get(
        `/rest/v2.4/events?cameraIds=${cameraId}&sortBy=eventId.desc` +
          (limit ? `&limit=${limit}` : "") +
          (types ? `&types=${types}` : "") +
          (minTimestamp ? `&minTimestamp=${encodeURIComponent(minTimestamp)}` : "") +
          (maxTimestamp ? `&maxTimestamp=${encodeURIComponent(maxTimestamp)}` : "") +
          (streams ? `&includeUrlTypes=${streams}` : ``),
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
          requestId: requestId,
        }
      );
      const events = response.data;
      events.map((e) => {
        e.timestamp = e.timestamp.replace("+0000", "Z");
      });
      return events;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_EVENTS"), error.detail);
    }
  }

  async getFirstCameraEvent(cameraId, requestId) {
    const maxTimestamp = new Date().toISOString().replace("Z", "+0000");
    try {
      const response = await axios.get(
        `/rest/v2.2/events?cameraIds=${cameraId}&sortBy=eventId.asc&limit=1&maxTimestamp=${encodeURIComponent(
          maxTimestamp
        )}`,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
          requestId: requestId,
        }
      );
      const event = response.data.length > 0 ? response.data[0] : null;
      if (event) event.timestamp = event.timestamp.replace("+0000", "Z");
      return event;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_FIRST_CAMERA_EVENT"), error.detail);
    }
  }

  async getEvent(eventId) {
    try {
      const response = await axios.get(`/rest/v2.4/events/${eventId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      const event = response.data;
      event.timestamp = event.timestamp.replace("+0000", "Z");
      return Promise.resolve(event);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_EVENT"), error.detail);
    }
  }

  async getEventSnapshot(eventId) {
    try {
      const response = await axios.get(`/rest/v2.3/events/${eventId}.jpg`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        responseType: "arraybuffer",
        headers: {
          Accept: "image/jpeg",
          Authorization: `Bearer ${this.token}`,
        },
      });
      const image = Buffer.from(response.data).toString("base64");
      return Promise.resolve(`data:${response.headers["content-type"].toLowerCase()};base64, ${image}`);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_EVENT_SNAPSHOT"), error.detail);
    }
  }

  async generateExportedVideo(cameraId, recordingId, videoExportRequest) {
    try {
      const response = await axios.post(
        `/rest/v2.4/cameras/${cameraId}/recordings/${recordingId}/export`,
        videoExportRequest,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.token}`,
          },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_VIDEO_EXPORT"), error.detail);
    }
  }

  async getExportedVideo(cameraId, recordingId, jobId) {
    try {
      const response = await axios.get(`/rest/v2.4/cameras/${cameraId}/recordings/${recordingId}/export/${jobId}.mov`, {
        responseType: "blob",
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GENERATE_EXPORTED_VIDEO"), error.detail);
    }
  }

  async getExportedVideoFromUrl(url) {
    try {
      const response = await axios.get(url, {
        responseType: "blob",
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_EXPORTED_VIDEO_FROM_URL"), error.detail);
    }
  }

  async generateVideoSummary(cameraId, body = {}) {
    try {
      const response = await axios.post(`/rest/v2.0/cameras/${cameraId}/summary`, body, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    } catch (reason) {
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("VIDEO_SUMMARY"), error.detail);
    }
  }

  async requestEndUserPasswordResetLink(email) {
    try {
      const response = await axios.post(
        "/auth/passwordResets/request?clientId=web.v2",
        `${encodeURIComponent("email")}=${encodeURIComponent(email)}`,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        }
      );

      return response.data;
    } catch (reason) {
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("REQUEST_PASSWORD_RESET"), error.detail);
    }
  }

  async getVideoSummery(cameraId, jobId) {
    try {
      const response = await axios.get(`/rest/v2.0/cameras/${cameraId}/summary/${jobId}.mp4`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        responseType: "arraybuffer",
        headers: {
          Accept: "video/mp4",
          Authorization: `Bearer ${this.token}`,
        },
      });
      return new Blob([response.data]);
    } catch (reason) {
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_VIDEO_SUMMARY"), error.detail);
    }
  }

  async addCameraByMAC(cameraName, zoneId, macAddress) {
    const data = {
      name: cameraName,
      zoneId: zoneId,
      hardwareId: {
        id: macAddress,
      },
    };
    try {
      const response = await axios.post(`/rest/v2.4/cameras`, data, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("ADD_CAMERA_MAC"), error.detail);
    }
  }

  async addDeviceByMAC(cameraName, zoneId, macAddress) {
    const data = {
      name: cameraName,
      zoneId: zoneId,
      hardwareId: {
        id: macAddress,
      },
    };
    try {
      const response = await axios.post(`/rest/v2.4/devices`, data, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("ADD_DEVICE"), error.detail);
    }
  }

  async addCameraStatusPoll(cameraId) {
    try {
      const response = await axios.get(`rest/v2.4/cameras/${cameraId}/additionStatus/poll`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    } catch (reason) {
      console.log("Poll", reason);
      let error = reason.response.data ? reason.response.data : reason.response;
      let detail = reason.response.data ? error.detail : error.title;
      throw new CustomError(error.code, this.getErrorCode("ADD_CAMERA_STATUS_POLL"), detail);
    }
  }

  async getDeviceConnectionCredentials(deviceId) {
    try {
      const response = await axios.get(`/rest/v2.4/devices/${deviceId}/connection/credentials`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_DEVICE_CONNECTION_CREDENTIALS"), error.detail);
    }
  } 

  async getDeviceConnectionDetails(deviceId) {
    try {
      const response = await axios.get(`/rest/v2.4/devices/${deviceId}/connection/details`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_DEVICE_CONNECTION_DETAILS"), error.detail);
    }
  }
  async getDevices() {
    try {
      const response = await axios.get(`/rest/v2.4/devices`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_ALL_DEVICES"), error.detail);
    }
  }


  async getChannels(deviceId) {
    try {
      const response = await axios.get(`/rest/v2.4/devices/${deviceId}/channels`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_ALL_CHANNELS"), error.detail);
    }
  }
  async setDeviceName(deviceId, name) {
    try {
      const response = await axios.patch(
        `/rest/v2.4/devices/${deviceId}`,
        { name: name },
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("PATCH_DEVICES"), error.detail);
    }
  }

  async getDeviceSnapShot(deviceId, channelId) {
    try {
      const response = await axios.get(
        `/rest/v2.4/devices/${deviceId}/channels/${channelId}/snapshot`, {
          baseURL: this.baseURL,
          responseType: "blob",
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_CHANNEL_SNAPSHOT"), error.detail);
    }
   }

   async getDeviceInfoBrand(deviceID) {
    try {
      const response = await axios.get(`/rest/v2.4/devices/${deviceID}/deviceInfo`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_DEVICE_INFO"), error.detail);
    }
  }

   async addCameraToChannel(deviceId, channelId ,cameraName , zone){
    try {
  
      const response = await axios.post(
        `/rest/v2.4/devices/${deviceId}/channels/${channelId}/camera`,
        { name: cameraName, zoneId: zone},
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("ADD_CHANNEL_AS_CAMERA"), error.detail);
    }
   }

   async deleteDevice(deviceID, password) {
    try {
      const response = await axios.delete(`/rest/v2.4/devices/${deviceID}?password=${password}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DELETE_DEVICE"), error.detail);
    }
  }


  async getDeviceInfo(deviceID) {
    try {
      const response = await axios.get(`/rest/v2.4/devices/${deviceID}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_DEVICE"), error.detail);
    }
  }

  async addDeviceStatusPoll(deviceID) {
    try {
      const response = await axios.get(`rest/v2.4/devices/${deviceID}/additionStatus/poll`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    } catch (reason) {
      console.log("Poll", reason);
      let error = reason.response.data ? reason.response.data : reason.response;
      let detail = reason.response.data ? error.detail : error.title;
      throw new CustomError(error.code, this.getErrorCode("ADD_DEVICE_STATUS_POLL"), detail);
    }
  }

  async deleteCamera(cameraId, password) {
    try {
      const response = await axios.delete(`rest/v2.3/cameras/${cameraId}?password=${password}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DELETE_CAMERA"), error.detail);
    }
  }

  async deleteChunks(cameraId, recordingId, firstPartId, lastPartId) {
    try {
      const response = await axios.delete(
        `/rest/v2.4/cameras/${cameraId}/recordings/${recordingId}/parts?firstRecordingPartId=${firstPartId}&lastRecordingPartId=${lastPartId}`,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.token}`,
          },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DELETE_RECORDING"), error.detail);
    }
  }

  async getUsers() {
    try {
      const response = await axios.get("/rest/v2.4/users", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_USERS"), error.detail);
    }
  }
  async addUser(user) {
    try {
      const response = await axios.post("/rest/v2.4/users", user, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("ADD_USER"), error.detail);
    }
  }
  async deleteUser(userId) {
    try {
      const response = await axios.delete(`/rest/v2.4/users/${userId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DELETE_USER"), error.detail);
    }
  }

  async getEntityByZohoId(zohoId) {
    try {
      if (!auth.isAuthorized()) return "Not authorized";

      const response = axios.get("/rest/v2.0/external/zoho/entities/" + zohoId, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: "Bearer " + auth.getToken() },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("DELETE_USER"), error.detail);
    }
  }

  async getRecordingSchedule(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.4/cameras/${cameraId}/schedules/recording`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return Promise.resolve(response.data);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_RECORDING_SCHEDULE"), error.detail);
    }
  }
  async setRecordingSchedule(cameraId, schedule) {
    try {
      const response = await axios.put(`/rest/v2.4/cameras/${cameraId}/schedules/recording`, schedule, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return Promise.resolve(response.data);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_RECORDING_SCHEDULE"), error.detail);
    }
  }
  async getMailNotificationSchedule(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.4/users/self/schedules/mailNotifications/cameras/${cameraId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return Promise.resolve(response.data);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_MAIL_NOTIFICATION_SCHEDULE"), error.detail);
    }
  }
  async setMailNotificationSchedule(cameraId, schedule) {
    try {
      const response = await axios.put(
        `/rest/v2.4/users/self/schedules/mailNotifications/cameras/${cameraId}`,
        schedule,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return Promise.resolve(response.data);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_MAIL_NOTIFICATION_SCHEDULE"), error.detail);
    }
  }
  async getPushNotificationSchedule(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.4/users/self/schedules/pushNotifications/cameras/${cameraId}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return Promise.resolve(response.data);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_PUSH_NOTIFICATION_SCHEDULE"), error.detail);
    }
  }
  async setPushNotificationSchedule(cameraId, schedule) {
    try {
      const response = await axios.put(
        `/rest/v2.4/users/self/schedules/pushNotifications/cameras/${cameraId}`,
        schedule,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return Promise.resolve(response.data);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("SET_PUSH_NOTIFICATION_SCHEDULE"), error.detail);
    }
  }
  async getCamerasLocations() {
    try {
      const response = await axios.get("/rest/v2.4/cameras/all/location", {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` }
      });
      return response.data
    } catch (reason) {
        console.error(reason);
        let error = reason.response.data;
        throw new CustomError(error.code, this.getErrorCode("GET_CAMERAS_LOCATION"), error.detail);
      }
  }
  async getCameraLocation(cameraId) {
    try {
      const response = await axios.get(`/rest/v2.4/cameras/${cameraId}/location`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` }
      });
      return response.data
    } catch (reason) {
        console.error(reason);
        let error = reason.response.data;
        throw new CustomError(error.code, this.getErrorCode("GET_CAMERA_LOCATION"), error.detail);
      }
  }
  async setCameraLocation(cameraId, cameraTimeZone) {
    try {
      const response = await axios.patch(`/rest/v2.4/cameras/${cameraId}/location`, 
      {
        timeZone: cameraTimeZone
      }, 
      {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` }
      });
      return response.data
    } catch (reason) {
        console.error(reason);
        let error = reason.response.data;
        throw new CustomError(error.code, this.getErrorCode("SET_CAMERA_LOCATION"), error.detail);
      }
  }

  async getNotificationSettings() {
    try {
      const response = await axios.get(`rest/v2.4/users/self/notificationSettings`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_NOTIFICATION_SETTINGS"), error.detail);
    }
  }

  async addNotificationSettings(notificationType, payload){
    try {
      const response = await axios.patch(
        `rest/v2.4/users/self/notificationSettings/${notificationType}`,
        payload,
        {
          baseURL: this.baseURL,
          cancelToken: this.source.token,
          headers: { Authorization: `Bearer ${this.token}` },
        }
      );
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("ADD_NOTIFICATION_SETTINGS"), error.detail);
    }
   }
  async getTimeZones() {
    try {
      const response = await axios.get('/rest/v2.4/timeZones', {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` }
      });
      return response.data
    } catch (reason) {
        console.error(reason);
        let error = reason.response.data;
        throw new CustomError(error.code, this.getErrorCode("GET_TIMEZONES"), error.detail);
      }
  }

  async getAccountTimeZone() {
    try {
      const response = await axios.get('/rest/v2.4/accounts/self/timeZone', {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` }
      });
      return response.data
    } catch (reason) {
        console.error(reason);
        let error = reason.response.data;
        throw new CustomError(error.code, this.getErrorCode("GET_ACCOUNT_TIMEZONE"), error.detail);
      }
  }

  async setAccountTimeZone(timeZone) {
    try {
      const response = await axios.patch('/rest/v2.4/accounts/self/timeZone', 
      {
        timeZone
      },
      {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: { Authorization: `Bearer ${this.token}` }
      });
      return response.data
    } catch (reason) {
        console.error(reason);
        let error = reason.response.data;
        throw new CustomError(error.code, this.getErrorCode("SET_ACCOUNT_TIMEZONE"), error.detail);
      }
  }

  async getLogs(minTimestamp, maxTimestamp) {
    try {
      const response = await axios.get(`/rest/v2.4/logs/?minTimestamp=${minTimestamp}&maxTimestamp=${maxTimestamp}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_LOGS"), error.detail);
    }
  }
  
  async getAvailableEventTypes(cameraIds) {
    try {
      const response = await axios.get(`/rest/v2.4/events/availableTypes?cameraIds=${cameraIds}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_AVAILABLE_EVENT_TYPES"), error.detail);
    }
  }
  
  async getEventTypes(language) {
    try {
      const response = await axios.get(`/rest/v2.4/events/eventTypeDetails${language ? `?language=${language}` : ""}`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
      return Promise.resolve(response.data);
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_EVENT_TYPES"), error.detail);
    }
  }

  async getFeatureFlags() {
    try {
      const response = await axios.get(`/rest/v2.4/users/self/featureFlags`, {
        baseURL: this.baseURL,
        cancelToken: this.source.token,
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
      return response.data;
    } catch (reason) {
      console.error(reason);
      let error = reason.response.data;
      throw new CustomError(error.code, this.getErrorCode("GET_FEATURE_FLAGS"), error.detail);
    }
  }
}

export default new CMApi();
